import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterComponent } from '../../../../shared/components/footer/footer.component';
import {
  AssetHeaderComponent,
  AssetHeaderConfig,
} from '../../../../shared/components/asset-header/asset-header.component';
import { DiscussionSidebarComponent } from '../../discussion-sidebar/discussion-sidebar.component';
import {
  DiscussionListComponent,
  DiscussionItem,
} from '../../shared/discussion-list/discussion-list.component';
import { DiscussionApiService } from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';
import { LoginModalComponent } from '../../../../shared/components/login-modal/login-modal.component';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { Router } from '@angular/router';
import { ToasterService } from '../../../../services/toaster.service';
import { DiscussionPaginationComponent } from '../../shared/discussion-pagination/discussion-pagination.component';
import { parseDiscussionPaginationMeta } from '../../shared/discussion-pagination.util';

@Component({
  selector: 'app-discussion-my-discussions',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    DiscussionSidebarComponent,
    DiscussionListComponent,
    DiscussionPaginationComponent,
    LoginModalComponent,
    ConfirmationDialogComponent,
  ],
  templateUrl: './my-discussions.component.html',
  styleUrl: './my-discussions.component.scss',
})
export class DiscussionMyDiscussionsComponent implements OnInit {
  private router = inject(Router);
  private discussionApiService = inject(DiscussionApiService);
  private keycloakService = inject(KeycloakService);
  private toasterService = inject(ToasterService);

  discussions: DiscussionItem[] = [];
  isLoggedIn: boolean = false;
  showLoginModal: boolean = false;
  showDeleteConfirmModal: boolean = false;
  discussionToDelete: DiscussionItem | null = null;
  deleteInProgress: boolean = false;
  activeTab: string = 'my-discussions';

  currentPage = 1;
  totalPages = 1;
  totalCount = 0;
  readonly itemsPerPage = 10;

  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Discussions', route: '/discussions' },
      { label: 'My Discussions' },
    ],
    title: 'My Discussions',
    description: 'View and manage your discussions',
  };

  ngOnInit(): void {
    this.checkLoginStatus();
    this.fetchMyDiscussions();
  }

  async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloakService.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      this.isLoggedIn = false;
    }
  }

  async fetchMyDiscussions(): Promise<void> {
    try {
      // Filters as per curl: {"type":null,"status":["APPROVED"],"tags":null}
      const response = await this.discussionApiService.getMyDiscussion({
        filters: {
          type: null,
          status: ['APPROVED'],
          tags: null,
        },
        sort_by: 'newest',
        page: this.currentPage,
        limit: this.itemsPerPage,
        pinned: true,
      });

      // Transform API response to DiscussionItem format
      const list = this.getDiscussionsListFromResponse(response);
      this.discussions = list.map((item: any) =>
        this.transformToDiscussionItem(item),
      );

      const pagination = parseDiscussionPaginationMeta(response);
      this.totalCount = pagination.totalCount || this.discussions.length;
      this.totalPages = pagination.totalPages;
    } catch (error) {
      console.error('Error fetching my discussions:', error);
      this.discussions = [];
      this.totalPages = 1;
      this.totalCount = 0;
    }
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.fetchMyDiscussions();
  }

  /** Extract discussions array from API response (handles data.discussions, data, etc.) */
  private getDiscussionsListFromResponse(response: any): any[] {
    if (!response) return [];
    const data = response.data ?? response;
    if (Array.isArray(data)) return data;
    if (data?.discussions && Array.isArray(data.discussions))
      return data.discussions;
    if (data?.discussion && Array.isArray(data.discussion))
      return data.discussion;
    return [];
  }

  transformToDiscussionItem(item: any): DiscussionItem {
    return {
      id: item.id || item._id || '',
      title: item.title || 'Untitled Discussion',
      description: item.description || item.content || '',
      author:
        item.user?.name ||
        item.author?.name ||
        item.author ||
        item.created_by ||
        'Unknown',
      category: item.category ?? '',
      discussionType:
        [item.type, item.discussion_type]
          .map((v) => (v != null && String(v).trim() ? String(v).trim() : ''))
          .find((s) => s) || undefined,
      replies: item.reply_count ?? item.replies ?? item.comment_count ?? 0,
      views: item.view_count ?? item.views ?? 0,
      lastActivity:
        item.updated_at ??
        item.updatedAt ??
        item.last_activity ??
        item.created_at ??
        item.createdAt ??
        new Date().toISOString(),
      createdAt: item.created_at ?? item.createdAt ?? undefined,
      isPinned: item.is_pinned ?? item.pinned ?? false,
      isBookmarked: item.is_bookmarked ?? item.bookmarked ?? false,
      tags: item.tags || [],
      attachments: item.attachments || [],
    };
  }

  createNewDiscussion(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    this.router.navigate(['/discussions/create-new']);
  }

  async onBookmarkClick(event: {
    event: Event;
    discussion: DiscussionItem;
  }): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const newBookmarkState = !event.discussion.isBookmarked;
    const previousState = event.discussion.isBookmarked;
    event.discussion.isBookmarked = newBookmarkState;
    try {
      if (previousState) {
        await this.discussionApiService.unbookmarkDiscussion(
          event.discussion.id,
        );
      } else {
        await this.discussionApiService.bookmarkDiscussion(event.discussion.id);
      }
      this.toasterService.success(
        newBookmarkState
          ? 'This discussion has been added to your bookmarks. You can open it anytime from My Bookmarks.'
          : 'This discussion has been removed from your bookmarks.',
      );
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      event.discussion.isBookmarked = previousState;
      this.toasterService.error(
        (error as any)?.error?.detail || 'Failed to update bookmark',
      );
    }
  }

  async onPinClick(event: {
    event: Event;
    discussion: DiscussionItem;
  }): Promise<void> {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const newPinState = !event.discussion.isPinned;
    const previousState = event.discussion.isPinned;
    event.discussion.isPinned = newPinState;
    try {
      if (previousState) {
        await this.discussionApiService.unpinDiscussion(event.discussion.id);
      } else {
        await this.discussionApiService.pinDiscussion(event.discussion.id);
      }
      this.toasterService.success(
        newPinState
          ? 'This discussion is now pinned. Find it under My Pinned in Discussions.'
          : 'This discussion has been unpinned.',
      );
    } catch (error) {
      console.error('Error toggling pin:', error);
      event.discussion.isPinned = previousState;
      this.toasterService.error(
        (error as any)?.error?.detail || 'Failed to update pin',
      );
    }
  }

  onEditClick(event: { event: Event; discussion: DiscussionItem }): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    this.router.navigate(['/discussions/create-new'], {
      queryParams: { edit: event.discussion.id },
    });
  }

  onDeleteClick(event: { event: Event; discussion: DiscussionItem }): void {
    this.discussionToDelete = event.discussion;
    this.showDeleteConfirmModal = true;
  }

  onCancelDelete(): void {
    this.showDeleteConfirmModal = false;
    this.discussionToDelete = null;
  }

  async onConfirmDelete(): Promise<void> {
    if (!this.discussionToDelete) return;
    this.deleteInProgress = true;
    try {
      await this.discussionApiService.deleteMyDiscussion(
        this.discussionToDelete.id,
      );
      this.discussions = this.discussions.filter(
        (d) => d.id !== this.discussionToDelete!.id,
      );
      this.toasterService.success('Discussion deleted successfully');
      this.showDeleteConfirmModal = false;
      this.discussionToDelete = null;
    } catch (error) {
      this.toasterService.error(
        (error as any)?.error?.detail || 'Failed to delete discussion',
      );
    } finally {
      this.deleteInProgress = false;
    }
  }
}
